/**
 * Multi-Contaminant Plume Layer
 *
 * Renders nested plumes for multiple contaminants using IDW voxel grids.
 * Each compound forms its own distinct plume with varying extents based on
 * physicochemical properties (retardation, biodegradation).
 *
 * Rendering:
 * - Measured plumes: PlumeVoxelCanvasLayer (160x160 IDW grid + edge feathering)
 * - Theoretical plumes: Streamline physics particles
 * - Compound-specific colors with concentration gradients
 */

import React, { useMemo } from 'react';
import { Polygon, Tooltip } from 'react-leaflet';
import { ContaminantResult, SiteGradientMap, RecoveryWell, ContaminantBoundaryData } from '../../api/polygon';
import {
  generateMultiContaminantPlumes,
  CompoundPlumeData,
  getCompoundColor,
  COMPOUND_COLORS,
} from './MultiContaminantPlumeUtils';
import { generateStreamlinePlume, PlumeParticle } from './StreamlinePlumeGenerator';
import { PlumeVoxelCanvasLayer, VoxelPlumeData, VoxelWell, VoxelCleanWell } from './PlumeVoxelCanvasLayer';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Predicted well concentration from physics engine.
 * Used to dynamically update plume based on future predictions.
 */
export interface PredictedWellConcentration {
  well_id: string;
  lat: number;
  lon: number;
  contaminant: string;
  concentration_ppb: number;
  exceeds_mcl: boolean;
  mcl_ppb: number | null;
}

export interface MultiContaminantPlumeLayerProps {
  centerLat: number;
  centerLon: number;
  baseLengthFt: number;
  baseWidthFt: number;
  flowDirection: number;
  contaminantResults: ContaminantResult[] | undefined;
  primaryContaminant: string;
  visible: boolean;
  gradientMap?: SiteGradientMap | null;
  recoveryWells?: RecoveryWell[];
  opacity?: number;
  // Individual compound visibility - if undefined, all compounds are visible
  visibleCompounds?: Set<string>;
  // Time index for selecting plume state (0 = T=0 seed plume)
  selectedTimeIndex?: number;
  // Well-based plume boundaries from API (overrides theoretical plumes)
  wellBoundaries?: ContaminantBoundaryData[];
  // Physics engine predictions - when provided, overrides measured concentrations
  predictedWellConcentrations?: PredictedWellConcentration[];
  // Prediction date for display
  predictionDate?: string;
}

// Extended particle with compound info
interface CompoundParticle extends PlumeParticle {
  compound: string;
  compoundColor: string;
}

// ============================================================================
// PLUME TABLE COMPONENT (for display outside map)
// ============================================================================

export function MultiContaminantTable({
  plumes,
  visible,
  visibleCompounds,
  onCompoundToggle,
  onToggleAll,
}: {
  plumes: CompoundPlumeData[];
  visible: boolean;
  visibleCompounds?: Set<string>;
  onCompoundToggle?: (compound: string, isVisible: boolean) => void;
  onToggleAll?: (showAll: boolean) => void;
}) {
  if (!visible || plumes.length === 0) return null;

  // Check if all compounds are visible (uses lowercase for consistency)
  const allVisible = !visibleCompounds || plumes.every(p => visibleCompounds.has(p.compound.toLowerCase()));
  const noneVisible = visibleCompounds && plumes.every(p => !visibleCompounds.has(p.compound.toLowerCase()));

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
          Multi-Contaminant Plume Extents
        </h3>
        {onToggleAll && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToggleAll(true)}
              className={`px-2 py-1 text-[10px] rounded transition-colors ${
                allVisible
                  ? 'bg-mono-700 text-mono-200'
                  : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
              }`}
            >
              Show All
            </button>
            <button
              onClick={() => onToggleAll(false)}
              className={`px-2 py-1 text-[10px] rounded transition-colors ${
                noneVisible
                  ? 'bg-mono-700 text-mono-200'
                  : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
              }`}
            >
              Hide All
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-mono-700">
              {onCompoundToggle && (
                <th className="text-center py-2 px-2 text-mono-400 font-medium w-8">Show</th>
              )}
              <th className="text-left py-2 px-2 text-mono-400 font-medium">Compound</th>
              <th className="text-right py-2 px-2 text-mono-400 font-medium">Length (ft)</th>
              <th className="text-right py-2 px-2 text-mono-400 font-medium">Width (ft)</th>
              <th className="text-right py-2 px-2 text-mono-400 font-medium">Conc (mg/L)</th>
              <th className="text-right py-2 px-2 text-mono-400 font-medium">MCL (mg/L)</th>
              <th className="text-right py-2 px-2 text-mono-400 font-medium">R</th>
              <th className="text-right py-2 px-2 text-mono-400 font-medium">t½ (days)</th>
            </tr>
          </thead>
          <tbody>
            {plumes.map((plume) => {
              const isCompoundVisible = !visibleCompounds || visibleCompounds.has(plume.compound.toLowerCase());
              return (
                <tr
                  key={plume.compound}
                  className={`border-b border-mono-800 hover:bg-mono-800/50 ${
                    !isCompoundVisible ? 'opacity-50' : ''
                  }`}
                >
                  {onCompoundToggle && (
                    <td className="text-center py-2 px-2">
                      <input
                        type="checkbox"
                        checked={isCompoundVisible}
                        onChange={(e) => onCompoundToggle(plume.compound, e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-mono-600 bg-mono-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: plume.color, opacity: isCompoundVisible ? 1 : 0.4 }}
                      />
                      <span className="text-mono-200 font-medium">{plume.compound}</span>
                    </div>
                  </td>
                  <td className="text-right py-2 px-2 text-mono-300 tabular-nums">
                    {plume.lengthFt.toLocaleString()}
                  </td>
                  <td className="text-right py-2 px-2 text-mono-300 tabular-nums">
                    {plume.widthFt.toLocaleString()}
                  </td>
                  <td className="text-right py-2 px-2 text-mono-300 tabular-nums">
                    {(plume.concentration_ug_l / 1000) < 0.01
                      ? (plume.concentration_ug_l / 1000).toExponential(2)
                      : (plume.concentration_ug_l / 1000).toFixed(3)}
                  </td>
                  <td className="text-right py-2 px-2">
                    {plume.mcl_ug_l ? (
                      <span className={plume.exceedsMcl ? 'text-red-400' : 'text-green-400'}>
                        {(plume.mcl_ug_l / 1000).toFixed(3)} {plume.exceedsMcl ? '⚠' : '✓'}
                      </span>
                    ) : (
                      <span className="text-mono-500">-</span>
                    )}
                  </td>
                  <td className="text-right py-2 px-2 text-mono-400 tabular-nums">
                    {plume.retardationFactor.toFixed(2)}
                  </td>
                  <td className="text-right py-2 px-2 text-mono-400 tabular-nums">
                    {plume.halfLifeDays === Infinity ? '∞' : plume.halfLifeDays}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-mono-500 mt-3 pt-2 border-t border-mono-700">
        Plume length driven by retardation (R) and biodegradation half-life (t½).
        Lower R and longer t½ = longer plume. Toggle checkboxes to show/hide individual plumes.
      </div>
    </div>
  );
}

// Keep legacy legend for backwards compatibility
export function MultiContaminantLegend({
  plumes,
  visible,
}: {
  plumes: CompoundPlumeData[];
  visible: boolean;
}) {
  if (!visible || plumes.length === 0) return null;

  return (
    <div className="absolute bottom-20 left-4 z-[1000] bg-mono-900/95 rounded-lg border border-mono-700 p-3 backdrop-blur-sm">
      <div className="text-xs font-medium text-mono-300 mb-2 uppercase tracking-wide">
        Nested Plume Extents
      </div>
      <div className="space-y-1.5">
        {plumes.map((plume) => (
          <div key={plume.compound} className="flex items-center gap-2">
            <div
              className="w-4 h-3 rounded-sm border border-white/30"
              style={{ backgroundColor: plume.color, opacity: 0.7 }}
            />
            <span className="text-xs text-mono-200 flex-1">
              {plume.compound}
            </span>
            <span className="text-xs text-mono-400 tabular-nums">
              {plume.lengthFt.toLocaleString()} ft
            </span>
            {plume.exceedsMcl && (
              <span className="text-[10px] text-red-400 font-medium">
                &gt;MCL
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate convex hull boundary from well locations.
 * Used when physics predictions change which wells exceed MCL.
 */
function generateConvexHullFromWells(
  wells: Array<{ lat: number; lon: number }>
): [number, number][] {
  if (wells.length < 3) {
    // Return a small circle around the wells if < 3
    if (wells.length === 0) return [];
    if (wells.length === 1) {
      // Create a small circle around single well
      const w = wells[0];
      const r = 0.0005; // ~180 ft radius
      return Array.from({ length: 8 }, (_, i) => {
        const angle = (i / 8) * 2 * Math.PI;
        return [w.lat + r * Math.cos(angle), w.lon + r * Math.sin(angle)] as [number, number];
      });
    }
    // Two wells - create an ellipse between them
    const [w1, w2] = wells;
    const midLat = (w1.lat + w2.lat) / 2;
    const midLon = (w1.lon + w2.lon) / 2;
    const dLat = Math.abs(w2.lat - w1.lat) / 2 + 0.0003;
    const dLon = Math.abs(w2.lon - w1.lon) / 2 + 0.0003;
    return Array.from({ length: 12 }, (_, i) => {
      const angle = (i / 12) * 2 * Math.PI;
      return [midLat + dLat * Math.cos(angle), midLon + dLon * Math.sin(angle)] as [number, number];
    });
  }

  // Sort points for convex hull (Graham scan)
  const points = wells.map(w => [w.lat, w.lon] as [number, number]);
  points.sort((a, b) => a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]);

  // Build lower hull
  const lower: [number, number][] = [];
  for (const p of points) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  // Build upper hull
  const upper: [number, number][] = [];
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  // Remove last point of each half (it's repeated)
  lower.pop();
  upper.pop();

  // Return raw hull — voxel edge feathering provides smooth visual buffer
  return [...lower, ...upper];
}

function cross(o: [number, number], a: [number, number], b: [number, number]): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

// ============================================================================
// MAIN COMPONENT - VOXEL GRID RENDERING WITH IDW CONCENTRATION GRADIENTS
// ============================================================================

export function MultiContaminantPlumeLayer({
  centerLat,
  centerLon,
  baseLengthFt,
  baseWidthFt,
  flowDirection,
  contaminantResults,
  primaryContaminant,
  visible,
  gradientMap,
  recoveryWells = [],
  opacity = 0.6,
  visibleCompounds,
  selectedTimeIndex = 0,
  wellBoundaries = [],
  predictedWellConcentrations = [],
  predictionDate,
}: MultiContaminantPlumeLayerProps) {
  // Generate plume metadata for all contaminants
  // PRIORITY:
  // 1. Physics predictions (if available) - dynamically updates exceedance wells
  // 2. Measured well boundaries from API
  // 3. Theoretical physics-based plumes
  const plumes = useMemo(() => {
    if (!visible) {
      return [];
    }

    // PRIORITY 1: If we have physics predictions, merge with measured boundaries
    // This allows the plume to dynamically adjust based on predicted future concentrations
    if (predictedWellConcentrations && predictedWellConcentrations.length > 0 && wellBoundaries && wellBoundaries.length > 0) {

      // Group predictions by contaminant
      const predictionsByContaminant = new Map<string, PredictedWellConcentration[]>();
      for (const pred of predictedWellConcentrations) {
        const key = pred.contaminant.toLowerCase();
        if (!predictionsByContaminant.has(key)) {
          predictionsByContaminant.set(key, []);
        }
        predictionsByContaminant.get(key)!.push(pred);
      }

      const fallbackColors = Object.values(COMPOUND_COLORS);
      return wellBoundaries.map((boundary, idx) => {
        const color = getCompoundColor(boundary.compound) || fallbackColors[idx % fallbackColors.length];
        const compoundKey = boundary.compound.toLowerCase();
        const predictions = predictionsByContaminant.get(compoundKey) || [];

        // If we have predictions for this contaminant, use them to update wells
        let wellsAbove = boundary.wells_with_detections || [];
        let wellsBelow = boundary.wells_below_threshold || [];
        let maxConc = boundary.max_concentration_ug_l;

        if (predictions.length > 0) {
          // Reclassify wells based on predicted concentrations
          const allWellIds = new Set([
            ...wellsAbove.map(w => w.well_id),
            ...wellsBelow.map(w => w.well_id),
          ]);

          wellsAbove = [];
          wellsBelow = [];
          maxConc = 0;

          for (const pred of predictions) {
            if (!allWellIds.has(pred.well_id)) continue;

            const wellData = {
              well_id: pred.well_id,
              lat: pred.lat,
              lon: pred.lon,
              concentration: pred.concentration_ppb,  // Already in ppb
              is_non_detect: pred.concentration_ppb < (pred.mcl_ppb || 5) * 0.1,
            };

            if (pred.exceeds_mcl) {
              wellsAbove.push(wellData);
              maxConc = Math.max(maxConc, pred.concentration_ppb);
            } else {
              wellsBelow.push(wellData);
            }
          }

        }

        // Regenerate boundary from exceedance wells only
        const newBoundary = wellsAbove.length >= 3
          ? generateConvexHullFromWells(wellsAbove)
          : boundary.boundary;

        return {
          compound: boundary.compound,
          polygon: newBoundary,
          wells: wellsAbove,
          wells_below_threshold: wellsBelow,
          lengthFt: 0,
          widthFt: 0,
          relativeLength: 1.0,
          concentration_ug_l: maxConc,
          mcl_ug_l: boundary.mcl_ug_l || 5.0,
          exceedsMcl: maxConc > (boundary.mcl_ug_l || 5.0),
          color,
          retardationFactor: 1.0,
          halfLifeDays: Infinity,
          sourceType: 'predicted' as const,
          predictionDate,
        };
      });
    }

    // PRIORITY 2: If we have well-based boundaries, use them directly
    // This creates plumes that reach only to wells with actual detections
    if (wellBoundaries && wellBoundaries.length > 0) {
      // Create plume data directly from well boundaries
      const fallbackColors = Object.values(COMPOUND_COLORS);
      return wellBoundaries.map((boundary, idx) => {
        const color = getCompoundColor(boundary.compound) || fallbackColors[idx % fallbackColors.length];
        return {
          compound: boundary.compound,
          polygon: boundary.boundary,
          wells: boundary.wells_with_detections || [],  // Include well data for concentration mapping
          wells_below_threshold: boundary.wells_below_threshold || [],  // Clean wells for exclusion zones
          lengthFt: 0,  // Will be calculated from polygon if needed
          widthFt: 0,
          relativeLength: 1.0,
          concentration_ug_l: boundary.max_concentration_ug_l,
          mcl_ug_l: boundary.mcl_ug_l || 5.0,
          exceedsMcl: boundary.max_concentration_ug_l > (boundary.mcl_ug_l || 5.0),
          color,
          retardationFactor: 1.0,
          halfLifeDays: Infinity,
          sourceType: 'measured' as const,
        };
      });
    }

    // No measured boundaries available — return empty.
    // Theoretical plumes are disabled to prevent flash before voxel grid loads.
    return [];
  }, [visible, contaminantResults, baseLengthFt, baseWidthFt, primaryContaminant, selectedTimeIndex, wellBoundaries, predictedWellConcentrations, predictionDate]);

  // Filter plumes to only visible compounds
  // Uses lowercase comparison for consistency with visibleCompounds state
  const visiblePlumes = useMemo(() => {
    if (!visibleCompounds) return plumes;  // All visible if no filter
    const filtered = plumes.filter(p => visibleCompounds.has(p.compound.toLowerCase()));
    return filtered;
  }, [plumes, visibleCompounds]);

  // Separate plumes with measured polygons from those needing particle rendering
  const measuredPlumes = visiblePlumes.filter(p => p.polygon && p.polygon.length >= 3);
  const theoreticalPlumes = visiblePlumes.filter(p => !p.polygon || p.polygon.length < 3);

  // Generate particles for THEORETICAL compounds using streamline physics
  // Uses same gradient contours AND recovery well sink physics as primary dissolved plume
  const allParticles = useMemo(() => {
    if (!visible || theoreticalPlumes.length === 0) {
      return [];
    }

    const particles: CompoundParticle[] = [];

    // Filter for enabled recovery wells only
    const enabledWells = (recoveryWells || []).filter(w => w.enabled !== false);

    // Generate particles for each theoretical compound WITH full physics
    for (const plume of theoreticalPlumes) {
      const streamlineResult = generateStreamlinePlume(
        centerLat,
        centerLon,
        gradientMap || null,
        enabledWells,
        plume.lengthFt,
        plume.widthFt,
        {
          numStreamlines: 35,
          stepsPerStreamline: 80,
          concentrationLevels: [0.95, 0.85, 0.7, 0.5, 0.3, 0.15],
        }
      );

      const compoundParticles: CompoundParticle[] = streamlineResult.particles.map(p => ({
        ...p,
        compound: plume.compound,
        compoundColor: plume.color,
      }));

      particles.push(...compoundParticles);
    }

    return particles;
  }, [visible, theoreticalPlumes, centerLat, centerLon, gradientMap, recoveryWells]);

  // Group particles by compound for layered rendering
  const particlesByCompound = useMemo(() => {
    if (allParticles.length === 0) {
      return [];
    }

    const grouped: Map<string, CompoundParticle[]> = new Map();

    for (const particle of allParticles) {
      const existing = grouped.get(particle.compound) || [];
      existing.push(particle);
      grouped.set(particle.compound, existing);
    }

    // Sort by plume length (longest first)
    const sorted = Array.from(grouped.entries()).sort((a, b) => {
      const plumeA = theoreticalPlumes.find(p => p.compound === a[0]);
      const plumeB = theoreticalPlumes.find(p => p.compound === b[0]);
      return (plumeB?.lengthFt || 0) - (plumeA?.lengthFt || 0);
    });

    return sorted;
  }, [allParticles, theoreticalPlumes]);

  // Map measured plumes to VoxelPlumeData for the voxel grid renderer
  // NOTE: This useMemo MUST be called before any early returns to follow React's rules of hooks
  const voxelPlumes = useMemo((): VoxelPlumeData[] => {
    if (!visible || measuredPlumes.length === 0) return [];

    return measuredPlumes
      .filter(p => p.polygon && p.polygon.length >= 3)
      .map((plumeData) => {
        // Use ALL wells (above + below threshold) for IDW concentration gradient
        // Exceeding wells pull gradient high, non-exceeding wells pull it low
        // This creates a natural concentration gradient across the plume
        const aboveWells: VoxelWell[] = ((plumeData as any).wells || [])
          .filter((w: any) => !w.is_non_detect)
          .map((w: any) => ({
            lat: w.lat,
            lon: w.lon,
            concentration: w.concentration,
          }));

        const belowWells: VoxelWell[] = (plumeData.wells_below_threshold || [])
          .filter((w: any) => !w.is_non_detect && w.concentration > 0)
          .map((w: any) => ({
            lat: w.lat,
            lon: w.lon,
            concentration: w.concentration,
          }));

        const allWells = [...aboveWells, ...belowWells];

        return {
          id: plumeData.compound,
          boundary: plumeData.polygon as [number, number][],
          wells: allWells,
          // No clean well exclusion — perimeter wells define the boundary,
          // IDW gradient handles concentration variation naturally
          maxConcentration: plumeData.concentration_ug_l,
          baseColor: plumeData.color,
          compound: plumeData.compound,
        };
      });
  }, [visible, measuredPlumes]);

  // Early return AFTER all hooks have been called
  if (!visible || (allParticles.length === 0 && measuredPlumes.length === 0)) {
    return null;
  }

  return (
    <>
      {/* VOXEL GRID LAYER — IDW concentration gradient with edge feathering */}
      <PlumeVoxelCanvasLayer
        plumes={voxelPlumes}
        gridResolution={160}
        edgeFeatherFt={15}
        baseOpacity={opacity}
        visible={visible && voxelPlumes.length > 0}
        zIndex={400}
      />

      {/* Invisible polygon overlays for hover tooltips - these are fine as React components */}
      {/* Only ~5 polygons total, not 15,000 */}
      {measuredPlumes.map((plumeData) => {
        const polygon = plumeData.polygon;
        if (!polygon || polygon.length < 3) return null;

        const polygonCoords: [number, number][] = polygon.map(([lat, lon]) => [lat, lon]);
        const wells = (plumeData as any).wells || [];
        const maxConc = plumeData.concentration_ug_l;

        // Calculate MCL for display
        const mclMgL = plumeData.mcl_ug_l ? (plumeData.mcl_ug_l / 1000).toFixed(3) : 'N/A';
        const maxConcMgL = maxConc ? (maxConc / 1000).toFixed(3) : '0';
        const exceedsMcl = plumeData.exceedsMcl;

        return (
          <Polygon
            key={`tooltip-${plumeData.compound}`}
            positions={polygonCoords}
            pathOptions={{
              color: 'transparent',
              fillColor: 'transparent',
              fillOpacity: 0,
              weight: 0,
            }}
            eventHandlers={{
              mouseover: (e) => {
                // Highlight the plume boundary on hover
                e.target.setStyle({
                  color: plumeData.color,
                  weight: 2,
                  opacity: 0.8,
                });
              },
              mouseout: (e) => {
                e.target.setStyle({
                  color: 'transparent',
                  weight: 0,
                });
              },
            }}
          >
            <Tooltip sticky>
              <div className="text-sm font-medium" style={{ minWidth: '180px' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: plumeData.color }}
                  />
                  <span className="font-semibold">{plumeData.compound}</span>
                </div>
                <div className="text-xs space-y-0.5 text-gray-600">
                  <div>Max Conc: <span className="font-mono">{maxConcMgL} mg/L</span></div>
                  <div>MCL: <span className="font-mono">{mclMgL} mg/L</span></div>
                  <div>
                    Status:{' '}
                    <span className={exceedsMcl ? 'text-red-600 font-medium' : 'text-green-600'}>
                      {exceedsMcl ? '⚠ Exceeds MCL' : '✓ Below MCL'}
                    </span>
                  </div>
                  <div>Wells: {wells.length} exceeding threshold</div>
                  {predictionDate && (
                    <div className="mt-1 pt-1 border-t border-gray-300">
                      Prediction: {new Date(predictionDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </Tooltip>
          </Polygon>
        );
      })}

      {/* THEORETICAL plumes temporarily disabled - only showing particle-based measured plumes */}
    </>
  );
}

// ============================================================================
// 3D SUPPORT - Generate particles for 3D view
// ============================================================================

export interface MultiContaminant3DParticle {
  lat: number;
  lon: number;
  concentration: number;
  radiusFt: number;
  compound: string;
  color: string;
}

/**
 * Generate particles for 3D multi-contaminant visualization.
 * @param visibleCompounds - Optional set of compound names to render. If undefined, all compounds are rendered.
 */
export function generateMultiContaminant3DParticles(
  centerLat: number,
  centerLon: number,
  contaminantResults: ContaminantResult[] | undefined,
  baseLengthFt: number,
  baseWidthFt: number,
  primaryContaminant: string,
  gradientMap: SiteGradientMap | null,
  recoveryWells: RecoveryWell[],
  visible: boolean,
  visibleCompounds?: Set<string>,
  selectedTimeIndex: number = 0  // Time index to get correct plume state
): MultiContaminant3DParticle[] {
  if (!visible || !contaminantResults || contaminantResults.length === 0) {
    return [];
  }

  const allPlumes = generateMultiContaminantPlumes(
    contaminantResults,
    baseLengthFt,
    baseWidthFt,
    primaryContaminant,
    selectedTimeIndex  // Pass time index to get correct polygon
  );

  // Filter to only visible compounds
  const plumes = visibleCompounds
    ? allPlumes.filter(p => visibleCompounds.has(p.compound))
    : allPlumes;

  if (plumes.length === 0) {
    return [];
  }

  const particles: MultiContaminant3DParticle[] = [];

  // Filter for enabled recovery wells - same sink physics as primary dissolved plume
  const enabledWells = (recoveryWells || []).filter(w => w.enabled !== false);

  for (const plume of plumes) {
    // PRIORITY: Use actual measured polygon if available
    // This ensures 3D view matches 2D map with same boundary shapes
    if (plume.polygon && plume.polygon.length >= 3) {

      // Generate particles that fill the actual polygon shape
      // Use polygon centroid as reference
      const centroidLat = plume.polygon.reduce((sum, p) => sum + p[0], 0) / plume.polygon.length;
      const centroidLon = plume.polygon.reduce((sum, p) => sum + p[1], 0) / plume.polygon.length;

      // Generate particles along the polygon boundary and interior
      // Boundary particles (outer edge)
      for (let i = 0; i < plume.polygon.length; i++) {
        const [lat, lon] = plume.polygon[i];
        particles.push({
          lat,
          lon,
          concentration: 0.3,  // Lower concentration at edges
          radiusFt: 15,
          compound: plume.compound,
          color: plume.color,
        });
      }

      // Interior particles at different concentration levels (70%, 40% toward center)
      for (const scaleFactor of [0.7, 0.4]) {
        for (let i = 0; i < plume.polygon.length; i += 2) {  // Every other vertex
          const [lat, lon] = plume.polygon[i];
          const scaledLat = centroidLat + (lat - centroidLat) * scaleFactor;
          const scaledLon = centroidLon + (lon - centroidLon) * scaleFactor;
          particles.push({
            lat: scaledLat,
            lon: scaledLon,
            concentration: 1 - scaleFactor + 0.3,  // Higher concentration toward center
            radiusFt: 20 - scaleFactor * 10,
            compound: plume.compound,
            color: plume.color,
          });
        }
      }

      // Core particle at centroid
      particles.push({
        lat: centroidLat,
        lon: centroidLon,
        concentration: 0.95,
        radiusFt: 25,
        compound: plume.compound,
        color: plume.color,
      });

      continue;  // Skip streamline generation for this compound
    }

    // FALLBACK: Use streamline physics for theoretical plumes (no measured polygon)
    // For shorter plumes (relative length < 0.5), use fewer wells to avoid over-capture
    // Short plumes like Toluene/Xylenes may not extend to downgradient wells
    const wellsForPlume = plume.relativeLength < 0.5
      ? enabledWells.filter((_, i) => i % 3 === 0)  // Use only 1/3 of wells
      : plume.relativeLength < 1.0
        ? enabledWells.filter((_, i) => i % 2 === 0)  // Use only half of wells
        : enabledWells;  // Full wells for longer plumes (MTBE, EDB)

    // Higher particle density for more visible/concentrated plumes in 3D
    // Uses same gradient contours AND recovery well sink physics as primary dissolved plume
    const streamlineResult = generateStreamlinePlume(
      centerLat,
      centerLon,
      gradientMap,
      wellsForPlume,  // Use filtered wells based on plume extent
      plume.lengthFt,
      plume.widthFt,
      {
        numStreamlines: 35,  // More streamlines for denser particle coverage
        stepsPerStreamline: 80,  // More particles along each streamline
        concentrationLevels: [0.95, 0.85, 0.7, 0.5, 0.3, 0.15],  // More concentration bands
      }
    );


    for (const p of streamlineResult.particles) {
      particles.push({
        lat: p.lat,
        lon: p.lon,
        concentration: p.concentration,
        radiusFt: p.radiusFt,
        compound: plume.compound,
        color: plume.color,
      });
    }
  }


  // Debug: Log sample particle positions
  if (particles.length > 0) {
    const sample = particles[Math.floor(particles.length / 2)];
  }
  return particles;
}

/**
 * Hook for 3D multi-contaminant plume data.
 */
export function useMultiContaminant3DPlumes({
  contaminantResults,
  baseLengthFt,
  baseWidthFt,
  primaryContaminant,
  visible,
}: {
  contaminantResults: ContaminantResult[] | undefined;
  baseLengthFt: number;
  baseWidthFt: number;
  primaryContaminant: string;
  visible: boolean;
}): CompoundPlumeData[] {
  return useMemo(() => {
    if (!visible || !contaminantResults || contaminantResults.length === 0) {
      return [];
    }

    return generateMultiContaminantPlumes(
      contaminantResults,
      baseLengthFt,
      baseWidthFt,
      primaryContaminant
    );
  }, [visible, contaminantResults, baseLengthFt, baseWidthFt, primaryContaminant]);
}

// ============================================================================
// EXPORT
// ============================================================================

export default MultiContaminantPlumeLayer;
